import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Palette, UtensilsCrossed, Building2, Megaphone, GraduationCap, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { GuidaJrButton } from "@/components/GuidaJrButton";
import { WaitlistModal } from "@/components/WaitlistModal";
import { api, WILDER_AVATAR } from "@/lib/api";
import ArtworkCard from "@/components/ArtworkCard";
import CountdownMeter from "@/components/CountdownMeter";
import VideoWall from "@/components/VideoWall";
import NewsletterForm from "@/components/NewsletterForm";

const CTAS = [
  { to: "/corsi", label: "Voglio imparare", desc: "Corsi, ricette d'arte e la chat con Wilder", Icon: GraduationCap },
  { to: "/servizi", label: "Ho un locale o struttura", desc: "Assaggi d'Arte in loco, consulenza HORECA", Icon: Building2 },
  { to: "/servizi", label: "Sono creator o azienda", desc: "Formazione AI, partnership con Wilder", Icon: Megaphone },
];

const FEATURED_IDS = ["wild-chef-jr", "corso-impiattamento", "cena-wild-chic"];

export default function Home() {
  const [artworks, setArtworks] = useState([]);
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [buying, setBuying] = useState(null);
  useEffect(() => {
    api.get("/artworks").then((r) => setArtworks(r.data.filter((a) => a.featured))).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    api.get("/shop/products").then((r) =>
      setFeatured(FEATURED_IDS.map((id) => r.data.find((p) => p.id === id)).filter(Boolean))
    ).catch(() => {});
  }, []);

  const buy = async (p) => {
    setBuying(p.id);
    try {
      const { data } = await api.post("/shop/checkout", { product_id: p.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Errore nell'avvio del pagamento, riprova.");
      setBuying(null);
    }
  };
  const [wlProduct, setWlProduct] = useState(null);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 fade-up">
            <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-6">Arte · Cibo · Eventi · AI</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-6">
              Dove i quadri si<br />mangiano e le ricette<br />si <span className="italic text-[#E4BD44]">appendono al muro</span>.
            </h1>
            <p className="text-base text-[#C9C4B8] leading-relaxed max-w-xl mb-10">
              Wildersquad è l'hub d'incontro della CREW: le cene evento Assaggi d'Arte dello Chef Antonio Blandolino,
              l'arte di suo padre Italo Blandolino e Wilderbro, il genio folle nato dai quadri che oggi parla,
              insegna e cucina con l'AI.
            </p>
            <div className="flex flex-col gap-px bg-[#2A2A2A] border border-[#2A2A2A] max-w-xl" data-testid="hero-ctas">
              {CTAS.map((c) => (
                <Link key={c.label} to={c.to} data-testid={`hero-cta-${c.label}`}
                  className="group bg-[#0A0A0A] hover:bg-[#141414] transition-colors px-6 py-5 flex items-center gap-5">
                  <c.Icon size={22} className="text-[#E4BD44] shrink-0" />
                  <div className="flex-1">
                    <p className="font-display text-lg sm:text-xl font-bold">{c.label}</p>
                    <p className="text-xs text-[#9a9484]">{c.desc}</p>
                  </div>
                  <ArrowRight size={18} className="text-[#9a9484] group-hover:text-[#E4BD44] group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 fade-up fade-up-2">
            <div className="art-frame aspect-[4/5] border border-[#2A2A2A]">
              <img
                src="/images/hero-assaggi.png"
                alt="Assaggi d'Arte" />
            </div>
            <p className="text-xs text-[#9a9484] mt-3 font-mono-ws">Assaggi d'Arte — la cena dello Chef Antonio Blandolino tra le opere di Italo</p>
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="border-b border-[#2A2A2A] bg-[#141414]">
        <div className="max-w-4xl mx-auto px-5 lg:px-10 py-16 text-center">
          <p className="font-display text-2xl sm:text-3xl leading-snug italic" data-testid="manifesto">
            "Crediamo che un piatto possa essere una pennellata, che una tela possa avere un profumo,
            e che il sacro abiti anche in una sfera di ricotta. Questo è il Wildersquad."
          </p>
        </div>
      </section>

      {/* Prossimo Assaggio */}
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-4">Prossimo evento</p>
            <h2 className="font-display text-lg font-bold mb-2">Assaggi d'Arte — Location Segreta</h2>
            <p className="text-sm text-[#C9C4B8] leading-relaxed mb-8">
              La cena-evento firmata dallo Chef Antonio Blandolino, ispirata alle opere di suo padre Italo, con asta live di riproduzioni numerate.
              Iscriviti con una caparra di €36,90: al raggiungimento di 30 partecipanti la location viene rivelata.
            </p>
            <Link to="/assaggi-darte" className="btn-primary" data-testid="home-event-cta">
              Entra in lista d'attesa <ArrowRight size={15} />
            </Link>
          </div>
          <CountdownMeter />
        </div>
      </section>

      {/* Wilder */}
      <section className="dark-section bg-[#121212] text-[#F9F6F0] border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-3 flex justify-center">
            <img src={WILDER_AVATAR} alt="Wilder" className="wilder-float w-44 h-44 object-cover rounded-full border-2 border-[#D4AF37]" />
          </div>
          <div className="lg:col-span-9">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Il genio folle</p>
            <h2 className="font-display text-lg font-bold mb-3">Wilder è uscito dai quadri. E adesso parla.</h2>
            <p className="text-sm text-[#b8b2a4] leading-relaxed max-w-2xl mb-7">
              Curioso, visionario, alchimista tra arte e cucina. Chiedigli una ricetta ispirata a un'opera,
              un consiglio per il tuo locale o una provocazione poetica: risponde sempre, e mai come ti aspetti.
            </p>
            <Link to="/wilder" className="btn-primary" data-testid="home-wilder-cta">
              <UtensilsCrossed size={15} /> Chatta con Wilder
            </Link>
          </div>
        </div>
      </section>

      {/* Galleria preview */}
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">Galleria Italo Blandolino</p>
              <h2 className="font-display text-lg font-bold">REBIRTH and CONTEMPORANEITY</h2>
            </div>
            <Link to="/galleria" className="ws-link text-sm font-medium hidden sm:block" data-testid="home-gallery-link">
              Tutta la collezione →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {artworks.map((a) => <ArtworkCard key={a.id} art={a} />)}
          </div>
        </div>
      </section>

      {/* Servizi */}
      <section className="border-b border-[#2A2A2A] bg-[#141414]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">Servizi & Consulenze</p>
          <h2 className="font-display text-lg font-bold mb-10">Tre pubblici, un solo squadrone</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
            {[
              { Icon: Palette, t: "Privati", d: "Corsi di cucina d'arte, coaching 1:1, esperienze Assaggi d'Arte." },
              { Icon: Building2, t: "Ristoranti · Hotel · Agriturismi", d: "Eventi Assaggi d'Arte in loco, consulenza menu, formazione staff." },
              { Icon: Megaphone, t: "Creator & Aziende", d: "Formazione AI, contenuti con Wilder, eventi brandizzati e partnership." },
            ].map((s, i) => (
              <Link to="/servizi" key={s.t} className="bg-[#0A0A0A] hover:bg-[#141414] transition-colors p-8 group" data-testid={`home-service-${i}`}>
                <s.Icon size={24} className="text-[#E4BD44] mb-5" />
                <h3 className="font-display text-xl font-bold mb-2">{s.t}</h3>
                <p className="text-sm text-[#C9C4B8] leading-relaxed mb-4">{s.d}</p>
                <span className="text-xs uppercase tracking-[0.15em] text-[#E4BD44] group-hover:underline">Scopri →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Video wall */}
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">Seguici</p>
          <h2 className="font-display text-lg font-bold mb-10">Guarda Wilder all'opera</h2>
          <VideoWall limit={6} />
        </div>
      </section>

      {/* Testimonianze */}
      <section className="border-b border-[#2A2A2A] bg-[#141414]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-10">
          {[
            ["«Non sapevo se stavo cenando o visitando una mostra. Poi ho capito: entrambe.»", "Ospite, Assaggi d'Arte"],
            ["«Il risotto della Ragazza delle Stelle è il piatto più poetico che abbia mai servito nel mio locale.»", "Ristoratore partner"],
          ].map(([q, a], i) => (
            <blockquote key={a} className="border-l-2 border-[#D4AF37] pl-6" data-testid={`testimonial-${i}`}>
              <p className="font-display text-xl italic leading-snug mb-3">{q}</p>
              <cite className="text-xs uppercase tracking-[0.15em] text-[#9a9484] not-italic">{a}</cite>
            </blockquote>
          ))}
        </div>
      </section>

      {/* Vetrina Shop */}
      {featured.length > 0 && (
        <section className="border-b border-[#2A2A2A]" data-testid="home-shop-showcase">
          <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">Shop</p>
                <h2 className="font-display text-lg font-bold">In evidenza dallo squadrone</h2>
              </div>
              <Link to="/shop" className="ws-link text-sm font-medium hidden sm:block" data-testid="home-shop-all">Tutto lo Shop →</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
              {featured.map((p) => (
                <div key={p.id} className="bg-[#0A0A0A] flex flex-col" data-testid={`home-featured-${p.id}`}>
                  <div className="aspect-[4/3] overflow-hidden border-b border-[#2A2A2A]">
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#E4BD44] mb-2">{p.category}</p>
                    <h3 className="font-display text-xl font-bold leading-snug mb-2">{p.name}</h3>
                    <p className="text-xs text-[#C9C4B8] leading-relaxed mb-5 flex-1">{p.desc}</p>
                    {p.category === "Esperienze" ? (
                      <div className="flex items-center justify-between">
                        <span className="font-display text-2xl font-black text-[#E4BD44]">€{p.price}</span>
                        <button onClick={() => buy(p)} disabled={buying === p.id}
                          className="btn-primary !py-2.5 !px-5 text-xs disabled:opacity-60" data-testid={`home-buy-${p.id}`}>
                          <ShoppingBag size={14} /> {buying === p.id ? "Un attimo…" : "Acquista Ora"}
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setWlProduct(p)}
                        className="btn-primary !py-2.5 !px-5 text-xs self-start" data-testid={`home-waitlist-${p.id}`}>
                        RICHIEDI INFO
                      </button>
                    )}
                    {p.id === "wild-chef-jr" && <GuidaJrButton className="mt-4" />}
                  </div>
                </div>
              ))}
            </div>
            <Link to="/shop" className="ws-link text-sm font-medium sm:hidden inline-block mt-6">Tutto lo Shop →</Link>
          </div>
          <WaitlistModal product={wlProduct || {}} open={!!wlProduct} onOpenChange={(v) => !v && setWlProduct(null)} />
        </section>
      )}

      {/* Blog preview */}
      <section className="border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#E4BD44] mb-3">Magazine</p>
              <h2 className="font-display text-lg font-bold">Dal blog Wildersquad</h2>
            </div>
            <Link to="/blog" className="ws-link text-sm font-medium hidden sm:block">Tutti gli articoli →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group" data-testid={`home-blog-${p.slug}`}>
                <div className="art-frame aspect-[16/10] border border-[#2A2A2A] mb-4">
                  <img src={p.cover_url} alt={p.title} loading="lazy" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#E4BD44] mb-2">{p.category}</p>
                <h3 className="font-display text-xl leading-snug group-hover:text-[#E4BD44] transition-colors">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[#141414]">
        <div className="max-w-3xl mx-auto px-5 lg:px-10 py-16 text-center">
          <h2 className="font-display text-lg font-bold mb-3">Le 3 ricette d'arte di Wilder, gratis</h2>
          <p className="text-sm text-[#C9C4B8] mb-8">Iscriviti alla newsletter: ricette ispirate ai quadri, eventi in anteprima, zero spam.</p>
          <div className="max-w-md mx-auto"><NewsletterForm testId="home-newsletter" /></div>
        </div>
      </section>
    </div>
  );
}
